import { useId } from 'react'
import type { RenderHelp, RenderSource } from '../../../insightRenderShapes'
import { InfoAccessoryButton } from '../../shared/PageHelp'
import { Note } from '../InsightsBlocks'
import { formatInsightDatum } from './formatInsightDatum'

export type PresentationHelp = RenderHelp

function SourceNote({ source }: { source: RenderSource }) {
  let value = 'Not available'
  if (source.source === 'observedWindow') {
    if (source.start && source.end)
      value = `${new Date(source.start).toLocaleString()} – ${new Date(source.end).toLocaleString()}`
  } else if (source.syncedAt) value = new Date(source.syncedAt).toLocaleString()
  return (
    <Note>
      {source.label}: {value}
    </Note>
  )
}

/** Shared shell; all analytical explanations and selected notes arrive from the server. */
export function InsightCardHelpContent({ help }: { help: PresentationHelp }) {
  return (
    <s-stack gap="small">
      <s-text fontWeight="semibold">Why this matters</s-text>
      <s-paragraph>{help.why}</s-paragraph>
      <s-text fontWeight="semibold">Where the numbers come from</s-text>
      <s-paragraph color="subdued">{help.how}</s-paragraph>
      {help.notes.map((note, noteIndex) => (
        <s-stack key={`${note.code}-${noteIndex}`} gap="small-100">
          <s-paragraph color="subdued">{note.text}</s-paragraph>
          {note.values.map((datum, index) => (
            <Note key={index}>
              {datum.label}: {formatInsightDatum(datum)}
            </Note>
          ))}
          {note.sources.map((source, index) => (
            <SourceNote key={index} source={source} />
          ))}
        </s-stack>
      ))}
    </s-stack>
  )
}

export function InsightCardHeader({ title, help }: { title: string; help: PresentationHelp }) {
  const id = useId()
  const popoverId = `insight-help-${id}`
  return (
    <>
      <InfoAccessoryButton id={popoverId} heading={title} />
      <s-popover id={popoverId} maxInlineSize="340px">
        <s-box padding="base">
          <InsightCardHelpContent help={help} />
        </s-box>
      </s-popover>
    </>
  )
}
